import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, FileText, Building2, Pencil, Send, Mail, XCircle,
  DollarSign, AlertCircle, CheckCircle2, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { can } from '@/types';
import {
  useInvoice, usePayments, useSendInvoice, useSendReminder, useVoidInvoice,
} from '@/hooks/useFinancials';
import { PAYMENT_METHOD_LABELS, PAYMENT_TERMS_LABELS } from '@/types/financials';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoaderScreen } from '@/components/ui/LoaderScreen';
import { EmptyState } from '@/components/ui/EmptyState';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { InvoiceStatusBadge } from '@/components/financials/InvoiceStatusBadge';
import { InvoiceFormModal } from '@/components/financials/InvoiceFormModal';
import { RecordPaymentModal } from '@/components/financials/RecordPaymentModal';
import { formatCurrency } from '@/lib/utils';

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = user ? can(user.role, 'financials', 'write') : false;

  const { data: invoice, isLoading, error } = useInvoice(id);
  const { data: payments } = usePayments(invoice ? { invoiceId: id } : {});

  const sendInvoice = useSendInvoice();
  const sendReminder = useSendReminder();
  const voidInvoice = useVoidInvoice();

  const [editOpen, setEditOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [confirmVoid, setConfirmVoid] = useState(false);

  // Only applications that touch THIS invoice
  const relevantApps = useMemo(() => {
    if (!payments || !id) return [];
    return payments.flatMap((p) =>
      p.applications
        .filter((a) => a.invoiceId === id)
        .map((a) => ({ ...a, payment: p })),
    ).sort((a, b) =>
      new Date(b.payment.receivedDate).getTime() - new Date(a.payment.receivedDate).getTime(),
    );
  }, [payments, id]);

  if (isLoading) return <LoaderScreen />;
  if (error || !invoice) {
    return (
      <Card>
        <EmptyState
          icon={AlertCircle}
          title="Could not load invoice"
          description={(error as { message?: string })?.message ?? 'Invoice not found.'}
          action={<Link to="/financials/invoices"><Button variant="secondary" leftIcon={<ArrowLeft size={14} />}>Back to Invoices</Button></Link>}
        />
      </Card>
    );
  }

  const overdue = invoice.status === 'past_due';
  const daysUntilDue = Math.floor(
    (new Date(invoice.dueDate).getTime() - Date.now()) / (24 * 60 * 60 * 1000),
  );

  async function onSend() {
    try {
      await sendInvoice.mutateAsync(invoice!.id);
      toast.success('Invoice sent via SES');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Send failed.');
    }
  }

  async function onRemind() {
    try {
      await sendReminder.mutateAsync(invoice!.id);
      toast.success('Reminder sent');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Reminder failed.');
    }
  }

  async function onVoid() {
    try {
      await voidInvoice.mutateAsync({ id: invoice!.id, reason: 'Voided from detail page' });
      toast.success(`Voided ${invoice!.number}`);
      navigate('/financials/invoices');
    } catch (err) {
      toast.error((err as { message?: string }).message ?? 'Void failed.');
    }
  }

  return (
    <div className="space-y-8">
      <Link to="/financials/invoices" className="inline-flex items-center gap-2 text-xs text-text-dim hover:text-text-muted">
        <ArrowLeft size={12} />
        <span className="kicker">All Invoices</span>
      </Link>

      {/* Hero */}
      <header className="flex items-start justify-between gap-6">
        <div className="flex items-start gap-5 min-w-0 flex-1">
          <div className="shrink-0 h-16 w-16 rounded-sm bg-walnut-700 border border-brass-400/30 flex items-center justify-center">
            <FileText size={22} className="text-brass-300" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <span className="kicker">Invoice</span>
              <InvoiceStatusBadge status={invoice.status} />
              {invoice.status === 'paid' && <CheckCircle2 size={14} className="text-success" />}
            </div>
            <h1 className="font-display text-4xl text-text leading-tight tracking-tight font-mono">
              {invoice.number}
            </h1>
            {invoice.orderNumber && (
              <div className="text-2xs text-text-dim mt-2">
                Billed for <Link to={`/orders`} className="text-brass-300 hover:text-brass-200 font-mono">{invoice.orderNumber}</Link>
              </div>
            )}
          </div>
        </div>

        {canWrite && invoice.status !== 'void' && (
          <div className="flex items-center gap-2 shrink-0">
            {invoice.status === 'draft' && (
              <Button leftIcon={<Send size={13} />} onClick={onSend} loading={sendInvoice.isPending}>
                Send Invoice
              </Button>
            )}
            {['sent', 'partial', 'past_due'].includes(invoice.status) && (
              <>
                <Button leftIcon={<DollarSign size={13} />} onClick={() => setPayOpen(true)}>
                  Record Payment
                </Button>
                <Button variant="secondary" leftIcon={<Mail size={13} />} onClick={onRemind} loading={sendReminder.isPending}>
                  Send Reminder
                </Button>
              </>
            )}
            {invoice.status !== 'paid' && (
              <Button variant="secondary" leftIcon={<Pencil size={13} />} onClick={() => setEditOpen(true)}>
                Edit
              </Button>
            )}
            <Button variant="ghost" onClick={() => setConfirmVoid(true)} aria-label="Void">
              <XCircle size={14} />
            </Button>
          </div>
        )}
      </header>

      {/* Meta */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border rounded-sm overflow-hidden border border-border">
        <Meta label="Client" value={
          <Link to={`/clients/${invoice.clientId}`} className="text-text hover:text-brass-300 inline-flex items-center gap-1.5">
            <Building2 size={12} />
            {invoice.clientName ?? invoice.clientId}
          </Link>
        } />
        <Meta
          label="Issued"
          value={invoice.issueDate ? new Date(invoice.issueDate).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—'}
        />
        <Meta
          label="Due"
          value={
            <span className="flex items-center gap-2">
              {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString('en-US', { dateStyle: 'medium' }) : '—'}
              {overdue && <span className="kicker text-danger">Overdue</span>}
              {!overdue && daysUntilDue >= 0 && daysUntilDue <= 7 && (
                <span className="kicker text-warning">In {daysUntilDue}d</span>
              )}
            </span>
          }
          alert={overdue}
        />
        <Meta
          label="Balance Due"
          value={formatCurrency(invoice.totals?.balanceDue ?? 0)}
          accent
          alert={overdue}
        />
      </div>

      {/* Body */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
        {/* Line items */}
        <Card className="p-0 overflow-hidden">
          <header className="px-6 py-4 border-b border-border">
            <div className="kicker mb-1">Line Items · {invoice.lineItems.length}</div>
            <h3 className="font-display text-lg text-text">Details</h3>
          </header>

          {invoice.lineItems.length === 0 ? (
            <EmptyState icon={FileText} title="No line items" description="Edit this invoice to add details." />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="kicker px-6 py-3 font-normal w-10">#</th>
                  <th className="kicker px-6 py-3 font-normal">Description</th>
                  <th className="kicker px-6 py-3 font-normal text-right">Qty</th>
                  <th className="kicker px-6 py-3 font-normal text-right">Price</th>
                  <th className="kicker px-6 py-3 font-normal text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lineItems.map((li, idx) => (
                  <tr key={li.id} className="border-b border-border last:border-0">
                    <td className="px-6 py-4 text-text-dim tabular">{String(idx + 1).padStart(2, '0')}</td>
                    <td className="px-6 py-4">
                      <div className="text-text">{li.description}</div>
                      {li.sku && <div className="text-2xs font-mono text-text-dim mt-0.5">#{li.sku}</div>}
                    </td>
                    <td className="px-6 py-4 text-right tabular text-text whitespace-nowrap">
                      {li.quantity} {li.unit ?? 'ea'}
                    </td>
                    <td className="px-6 py-4 text-right tabular text-text-muted">
                      {formatCurrency(li.unitPrice)}
                    </td>
                    <td className="px-6 py-4 text-right tabular text-text">
                      {formatCurrency(li.quantity * li.unitPrice)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {/* Sidebar — totals + payments */}
        <div className="space-y-4">
          {/* Totals */}
          <Card>
            <div className="kicker mb-4">Totals</div>
            <dl className="space-y-2 text-sm">
              <Row label="Subtotal" value={formatCurrency(invoice.totals?.subtotal ?? 0)} />
              {(invoice.totals?.discount ?? 0) > 0 && (
                <Row label="Discount" value={`− ${formatCurrency(invoice.totals?.discount ?? 0)}`} muted />
              )}
              {(invoice.totals?.tax ?? 0) > 0 && (
                <Row label={`Tax (${((invoice.totals?.taxRate ?? 0) * 100).toFixed(2)}%)`} value={formatCurrency(invoice.totals?.tax ?? 0)} muted />
              )}
              {(invoice.totals?.shipping ?? 0) > 0 && (
                <Row label="Shipping" value={formatCurrency(invoice.totals?.shipping ?? 0)} muted />
              )}
              <div className="pt-2 mt-2 border-t border-border flex items-baseline justify-between">
                <dt className="text-text-muted">Total</dt>
                <dd className="tabular text-text">{formatCurrency(invoice.totals?.total ?? 0)}</dd>
              </div>
              {(invoice.totals?.amountPaid ?? 0) > 0 && (
                <Row label="Paid" value={`− ${formatCurrency(invoice.totals?.amountPaid ?? 0)}`} muted />
              )}
              <div className="pt-3 mt-3 border-t border-border-strong flex items-baseline justify-between">
                <dt className="kicker">Balance Due</dt>
                <dd className="font-display text-2xl text-brass-300 tabular">
                  {formatCurrency(invoice.totals?.balanceDue ?? 0)}
                </dd>
              </div>
            </dl>
          </Card>

          {/* Terms card */}
          <Card>
            <div className="kicker mb-2">Payment Terms</div>
            <div className="text-sm text-text">{PAYMENT_TERMS_LABELS[invoice.terms]}</div>
            {invoice.sentAt && (
              <div className="text-2xs text-text-dim mt-3 flex items-center gap-1.5">
                <Clock size={11} />
                Sent {new Date(invoice.sentAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
              </div>
            )}
            {invoice.lastReminderAt && (
              <div className="text-2xs text-text-dim mt-1 flex items-center gap-1.5">
                <Mail size={11} />
                Last reminder {new Date(invoice.lastReminderAt).toLocaleDateString('en-US', { dateStyle: 'medium' })}
              </div>
            )}
          </Card>

          {/* Payments applied */}
          <Card className="p-0 overflow-hidden">
            <header className="px-6 py-4 border-b border-border">
              <div className="kicker mb-1">Payments · {relevantApps.length}</div>
              <h3 className="font-display text-base text-text">Applied</h3>
            </header>
            {relevantApps.length === 0 ? (
              <div className="p-6 text-sm text-text-dim text-center">No payments applied yet.</div>
            ) : (
              <ul className="divide-y divide-border">
                {relevantApps.map((a) => (
                  <li key={`${a.payment.id}-${a.invoiceId}`} className="px-6 py-4">
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="font-mono text-xs text-brass-300">{a.payment.number}</span>
                      <span className="text-sm tabular text-success font-medium">
                        + {formatCurrency(a.amount)}
                      </span>
                    </div>
                    <div className="text-2xs text-text-dim">
                      {PAYMENT_METHOD_LABELS[a.payment.method]}
                      {a.payment.reference && ` · ${a.payment.reference}`}
                      {' · '}
                      {new Date(a.payment.receivedDate).toLocaleDateString('en-US', { dateStyle: 'medium' })}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>

          {/* Notes */}
          {invoice.notes && (
            <Card>
              <div className="kicker mb-2">Notes</div>
              <p className="text-sm text-text-muted whitespace-pre-wrap leading-relaxed">{invoice.notes}</p>
            </Card>
          )}

          {invoice.termsAndConditions && (
            <Card>
              <div className="kicker mb-2">Terms &amp; Conditions</div>
              <p className="text-xs text-text-dim whitespace-pre-wrap leading-relaxed">{invoice.termsAndConditions}</p>
            </Card>
          )}
        </div>
      </div>

      <InvoiceFormModal open={editOpen} onClose={() => setEditOpen(false)} invoice={invoice} />
      <RecordPaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        defaultClientId={invoice.clientId}
        defaultInvoiceId={invoice.id}
      />
      <ConfirmDialog
        open={confirmVoid}
        onClose={() => setConfirmVoid(false)}
        onConfirm={onVoid}
        loading={voidInvoice.isPending}
        destructive
        title={`Void ${invoice.number}?`}
        confirmLabel="Void invoice"
        description="A voided invoice cannot be reactivated. Its balance is removed from AR. Payments applied to this invoice remain but become unapplied credits on the client."
      />
    </div>
  );
}

function Meta({ label, value, accent, alert }: { label: string; value: React.ReactNode; accent?: boolean; alert?: boolean }) {
  return (
    <div className="bg-bg-panel p-4">
      <div className="kicker mb-1.5">{label}</div>
      <div className={`text-sm tabular ${accent ? 'font-display text-2xl text-brass-300' : alert ? 'text-danger' : 'text-text'}`}>
        {value}
      </div>
    </div>
  );
}

function Row({ label, value, muted }: { label: string; value: string; muted?: boolean }) {
  return (
    <div className="flex items-baseline justify-between">
      <dt className={muted ? 'text-text-dim' : 'text-text-muted'}>{label}</dt>
      <dd className={`tabular ${muted ? 'text-text-muted' : 'text-text'}`}>{value}</dd>
    </div>
  );
}
